import React from 'react';
import { PLATFORMS } from '../platform/platformConfig';

/** Paths not listed in primary nav but valid for any logged-in user */
export const ALWAYS_ALLOWED_PATH_PREFIXES = [
  '/profile',
  '/team',
  '/security',
  '/videos',
  '/credit-usage',
];

/** Agent create/edit and collections stay reachable on the events platform even when hidden from primary nav */
export const EVENTS_ALLOWED_PATH_PREFIXES = ['/agents', '/collections'];

const primarySidebarTree = [
  {
    key: '',
    label: 'Dashboard',
    icon: 'AppStoreOutlined',
    sectionname: 'dashboard',
    path: '/',
    commandGroup: 'Go to',
    description: 'Overview, performance, and account activity',
    keywords: ['home', 'dashboard'],
  },
  {
    key: 'agents-parent',
    label: 'Agents',
    icon: 'LineChartOutlined',
    sectionname: 'campaigns',
    commandGroup: 'Go to',
    keywords: ['agents', 'campaigns'],
    children: [
      {
        key: 'agents',
        label: 'Dashboard',
        path: '/agents/dashboard',
        paletteLabel: 'Agents — Dashboard',
        description: 'View and manage live agents',
        keywords: ['agents', 'campaigns'],
      },
      {
        key: 'agents-draft',
        label: 'Drafts',
        path: '/agents-draft',
        paletteLabel: 'Agents — Drafts',
        description: 'Resume in-progress agent drafts',
        keywords: ['agents', 'draft'],
      },
    ],
  },
  {
    key: 'screens',
    label: 'Screens',
    icon: 'ScreenOutlined',
    sectionname: 'screens',
    commandGroup: 'Go to',
    keywords: ['screens', 'engagement'],
    children: [
      {
        key: 'engagement',
        label: 'Engagements',
        path: '/engagement/dashboard',
        paletteLabel: 'Screens — Engagements',
        description: 'Manage engagement screen dashboards',
        keywords: ['engagement', 'screens'],
      },
      {
        key: 'cta',
        label: 'CTA',
        path: '/cta/dashboard',
        paletteLabel: 'Screens — CTA',
        description: 'Manage call-to-action screens',
        keywords: ['cta', 'screens', 'call to action'],
      },
      {
        key: 'endscreen',
        label: 'End screens',
        path: '/endscreen/dashboard',
        paletteLabel: 'Screens — End Screens',
        description: 'Manage end screen experiences',
        keywords: ['end screen', 'screens'],
      },
      {
        key: 'screens-draft',
        label: 'Drafts',
        path: '/screens-draft',
        paletteLabel: 'Screens — Drafts',
        description: 'Resume in-progress screen drafts',
        keywords: ['screens', 'draft'],
      },
    ],
  },
  {
    key: 'collections-parent',
    label: 'Collections',
    icon: 'FolderOutlined',
    sectionname: 'screens',
    commandGroup: 'Go to',
    keywords: ['collections'],
    children: [
      {
        key: 'collections',
        label: 'Knowledge Base',
        path: '/collections/dashboard',
        paletteLabel: 'Collections — Knowledge Base',
        description: 'Organise content into collections',
        keywords: ['collections', 'collections v2', 'knowledge base', 'screens'],
      },
      {
        key: 'recirculations',
        label: 'Recirculation',
        path: '/recirculations/dashboard',
        paletteLabel: 'Collections — Recirculation',
        description: 'Manage recirculation collections',
        keywords: ['collections', 'recirculation', 'screens', 'action bucket'],
      },
    ],
  },
  {
    key: '6',
    label: 'Websites',
    icon: 'GlobalOutlined',
    sectionname: 'website',
    commandGroup: 'Go to',
    keywords: ['website', 'domains'],
    children: [
      {
        key: 'domains',
        label: 'Domains',
        path: '/domains',
        paletteLabel: 'Websites — Domains',
        description: 'The websites your agents run on',
        keywords: ['domains', 'website'],
      },
    ],
  },
  {
    key: 'aiLabs-parent',
    label: 'AI Labs',
    icon: 'AIStarOutlined',
    sectionname: 'aiLabs',
    commandGroup: 'Go to',
    keywords: ['ai labs', 'engines', 'fine tuning'],
    children: [
      {
        key: 'aiLabs',
        label: 'Engines',
        path: '/aiLabs/dashboard',
        paletteLabel: 'AI Labs — Engines',
        description: 'Manage AI engine configurations',
        keywords: ['ai labs', 'engines'],
      },
      {
        key: 'fine-tuning',
        label: 'Fine tuning',
        path: '/fine-tuning/dashboard',
        paletteLabel: 'AI Labs — Fine Tuning',
        description: 'Train and refine AI models',
        keywords: ['ai labs', 'fine tuning'],
      },
    ],
  },
  {
    key: 'abTest',
    label: 'A/B tests',
    icon: 'TogglesOutline',
    sectionname: 'campaignABTest',
    path: '/abTest',
    commandGroup: 'Go to',
    description: 'Manage A/B tests for campaigns',
    keywords: ['ab test', 'split test'],
  },
];

const eventsPrimarySidebarTree = [
  {
    key: '',
    label: 'Dashboard',
    icon: 'AppStoreOutlined',
    sectionname: 'dashboard',
    path: '/',
    commandGroup: 'Go to',
    description: 'Overview, performance, and account activity',
    keywords: ['home', 'dashboard'],
  },
  {
    key: 'events',
    label: 'Events',
    icon: 'CalendarOutlined',
    sectionname: 'campaigns',
    path: '/events',
    commandGroup: 'Go to',
    description: 'Manage events and their workflows',
    keywords: ['events', 'workflows'],
  },
  {
    key: 'collections',
    label: 'Knowledge Base',
    icon: 'FolderOutlined',
    sectionname: 'screens',
    path: '/collections/dashboard',
    commandGroup: 'Go to',
    description: 'Organise content into collections',
    keywords: ['collections', 'knowledge base'],
  },
  primarySidebarTree.find(item => item.key === 'screens'),
  primarySidebarTree.find(item => item.key === '6'),
  {
    key: 'aiLabs-parent',
    label: 'AI Labs',
    icon: 'AIStarOutlined',
    sectionname: 'aiLabs',
    commandGroup: 'Go to',
    keywords: ['ai labs', 'engines', 'fine tuning'],
    children: [
      {
        key: 'aiLabs',
        label: 'Engines',
        path: '/aiLabs/dashboard',
        paletteLabel: 'AI Labs — Engines',
        description: 'Manage AI engine configurations',
        keywords: ['ai labs', 'engines'],
      },
      {
        key: 'fine-tuning',
        label: 'Fine tuning',
        path: '/fine-tuning/dashboard',
        paletteLabel: 'AI Labs — Fine Tuning',
        description: 'Train and refine AI models',
        keywords: ['ai labs', 'fine tuning'],
      },
    ],
  },
  {
    key: 'abTest',
    label: 'A/B tests',
    icon: 'TogglesOutline',
    sectionname: 'campaignABTest',
    path: '/abTest',
    commandGroup: 'Go to',
    description: 'Manage A/B tests for campaigns',
    keywords: ['ab test', 'split test'],
  },
].filter(Boolean);

export function getPrimarySidebarTree(platform = PLATFORMS.MEDIA) {
  return platform === PLATFORMS.EVENTS ? eventsPrimarySidebarTree : primarySidebarTree;
}

const settingsSidebarTree = [
  {
    key: 'profile',
    label: 'Profile',
    path: '/profile',
    commandGroup: 'Settings',
    keywords: ['profile', 'account'],
  },
  {
    key: 'team',
    label: 'Team',
    path: '/team',
    commandGroup: 'Settings',
    keywords: ['team', 'members'],
  },
  {
    key: 'security',
    label: 'Security',
    path: '/security',
    commandGroup: 'Settings',
    keywords: ['security', 'password'],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    commandGroup: 'Settings',
    path: '/integrations',
  },
  {
    key: 'credit-usage',
    label: 'Credit usage',
    path: '/credit-usage',
    commandGroup: 'Settings',
    keywords: ['credit', 'usage', 'billing'],
    requiresCreditAccess: true,
  },
  {
    key: 'videos',
    label: 'Videos',
    path: '/videos',
    commandGroup: 'Settings',
    keywords: ['videos', 'tutorials', 'help'],
    showVideosBadge: true,
  },
];

const secondarySidebarItems = [
  {
    key: 'sponsors',
    label: 'Sponsors',
    icon: 'DollarSignOutlined',
    // Sponsors access is currently grouped under AI Labs team permission.
    sectionname: 'aiLabs',
    path: '/sponsors',
    commandGroup: 'Go to',
    description: 'Manage sponsor placements',
    keywords: ['sponsors'],
    requiresSponsorsActive: true,
  },
  {
    key: 'leads',
    label: 'Leads',
    icon: 'UserGroupOutlined',
    sectionname: 'leads',
    path: '/leads',
    commandGroup: 'Go to',
    description: 'Review captured leads',
    keywords: ['leads'],
  },
];

export function buildPrimaryNavItems(handleClick, getIcon, platform = PLATFORMS.MEDIA) {
  return getPrimarySidebarTree(platform).map(item => {
    const navItem = {
      key: item.key,
      label: item.label,
      sectionname: item.sectionname,
      path: item.path,
    };

    if (item.icon) {
      navItem.icon = getIcon(item.icon);
    }

    if (item.path) {
      navItem.onClick = () => handleClick(item.path);
    }

    if (item.children) {
      navItem.children = item.children.map(child => ({
        key: child.key,
        label: child.label,
        path: child.path,
        onClick: () => handleClick(child.path),
      }));
    }

    return navItem;
  });
}

export function buildBottomNavItems({
  handleClick,
  getIcon,
  hasCreditAccess,
  isSponsorsActive,
  showVideosNotification,
  MenuBadge,
  platform = PLATFORMS.MEDIA,
}) {
  const settingChildren = settingsSidebarTree
    .filter(item => {
      if (item.requiresCreditAccess && !hasCreditAccess) return false;
      return true;
    })
    .map(item => {
      const child = {
        key: item.key,
        path: item.path,
        onClick: () => handleClick(item.path),
      };

      if (item.showVideosBadge) {
        child.label = (
          <MenuBadge
            label={item.label}
            showDot={showVideosNotification}
            color="var(--primary-Color)"
          />
        );
      } else {
        child.label = item.label;
      }

      return child;
    });

  const items = [
    ...(isSponsorsActive
      ? secondarySidebarItems
          .filter(item => item.requiresSponsorsActive)
          .map(item => ({
            key: item.key,
            label: item.label,
            icon: getIcon(item.icon),
            sectionname: item.sectionname,
            path: item.path,
            onClick: () => handleClick(item.path),
          }))
      : []),
    ...secondarySidebarItems
      .filter(item => {
        if (item.requiresSponsorsActive) return false;
        return true;
      })
      .map(item => ({
        key: item.key,
        label: item.label,
        icon: getIcon(item.icon),
        sectionname: item.sectionname,
        path: item.path,
        onClick: () => handleClick(item.path),
      })),
    {
      key: '7',
      label: (
        <MenuBadge label="Settings" showDot={showVideosNotification} color="var(--primary-Color)" />
      ),
      icon: getIcon('SettingOutlined'),
      children: settingChildren,
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: getIcon('LogoutOutlined'),
    },
  ];

  return items;
}

function flattenTreeItem(item, parentSectionname, parentIcon, parentGroup) {
  const commands = [];
  const sectionname = item.sectionname || parentSectionname;
  const icon = item.icon || parentIcon;
  const group = item.commandGroup || parentGroup || 'Navigation';

  if (item.path && !item.excludeFromCommandPalette) {
    commands.push({
      id: item.key || item.path,
      label: item.paletteLabel || item.label,
      description: item.description || '',
      group,
      path: item.path,
      sectionname,
      icon,
      keywords: item.keywords || [],
    });
  }

  if (item.children) {
    item.children.forEach(child => {
      commands.push(
        ...flattenTreeItem(
          {
            ...child,
            key: child.key,
            sectionname,
            icon,
            commandGroup: group,
          },
          sectionname,
          icon,
          group
        )
      );
    });
  }

  return commands;
}

export function getNavigationCommands(platform = PLATFORMS.MEDIA) {
  const commands = [];

  getPrimarySidebarTree(platform).forEach(item => {
    commands.push(...flattenTreeItem(item));
  });

  secondarySidebarItems.forEach(item => {
    if (item.path) {
      commands.push({
        id: item.key,
        label: item.label,
        description: item.description || '',
        group: item.commandGroup,
        path: item.path,
        sectionname: item.sectionname,
        icon: item.icon,
        keywords: item.keywords || [],
        requiresSponsorsActive: item.requiresSponsorsActive || false,
      });
    }
  });

  return commands;
}

export function getSettingsCommands() {
  return settingsSidebarTree.map(item => ({
    id: item.key,
    label: item.label,
    description:
      item.key === 'profile'
        ? 'Update your account details'
        : item.key === 'team'
          ? 'Invite and manage team members'
          : item.key === 'security'
            ? 'Change password and security settings'
            : item.key === 'credit-usage'
              ? 'Monitor credit consumption'
              : item.key === 'videos'
                ? 'Watch product tutorials and guides'
                : '',
    group: item.commandGroup,
    path: item.path,
    sectionname: null,
    icon: 'SettingOutlined',
    keywords: item.keywords || [],
    requiresCreditAccess: item.requiresCreditAccess || false,
  }));
}

export { primarySidebarTree };
